import { formatLocalDateTime } from '@shared/utils/dateTime';
import { friendlyLabel } from '@shared/utils/friendlyLabels';
import { statusPillClassName } from '@shared/utils/statusPill';

export type ApiRecord = Record<string, unknown>;

export type ControlFilter =
  | 'ALL'
  | 'PASS'
  | 'PARTIAL'
  | 'FAIL'
  | 'NOT_APPLICABLE'
  | 'CLIENT_ACTION_REQUIRED'
  | 'HUMAN_PROCESS_REQUIRED'
  | 'POSSIBLE_NOT_APPLICABLE'
  | 'FINAL_ASSESSOR_REVIEW_PENDING';

export const CONTROL_FILTERS: Array<{ value: ControlFilter; label: string }> = [
  { value: 'ALL', label: 'All controls' },
  { value: 'PASS', label: 'PASS' },
  { value: 'PARTIAL', label: 'PARTIAL' },
  { value: 'FAIL', label: 'FAIL' },
  { value: 'NOT_APPLICABLE', label: 'NOT_APPLICABLE' },
  { value: 'CLIENT_ACTION_REQUIRED', label: 'Client action required' },
  { value: 'HUMAN_PROCESS_REQUIRED', label: 'Human/process required' },
  { value: 'POSSIBLE_NOT_APPLICABLE', label: 'Possible not applicable' },
  { value: 'FINAL_ASSESSOR_REVIEW_PENDING', label: 'Final assessor review pending' },
];

export function asRecord(value: unknown): ApiRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as ApiRecord) : {};
}

export function asArray(value: unknown): ApiRecord[] {
  return Array.isArray(value) ? value.filter((item): item is ApiRecord => Boolean(item) && typeof item === 'object' && !Array.isArray(item)) : [];
}

export function recordsFromResponse(response: unknown, keys: string[]): ApiRecord[] {
  const root = asRecord(response);
  for (const key of keys) {
    const records = asArray(root[key]);
    if (records.length > 0) return records;
  }
  const nestedKeys = ['data', 'result', 'payload'];
  for (const nestedKey of nestedKeys) {
    const nested = asRecord(root[nestedKey]);
    for (const key of keys) {
      const records = asArray(nested[key]);
      if (records.length > 0) return records;
    }
  }
  return [];
}

export function recordFromResponse(response: unknown, keys: string[]): ApiRecord {
  const root = asRecord(response);
  for (const key of keys) {
    const record = asRecord(root[key]);
    if (Object.keys(record).length > 0) return record;
  }
  return Object.keys(root).length > 0 ? root : {};
}

export function text(record: ApiRecord | undefined, ...keys: string[]): string {
  const item = record ?? {};
  for (const key of keys) {
    const value = item[key];
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      const joined = value
        .map((entry) => String(entry ?? '').trim())
        .filter(Boolean)
        .join(', ');
      if (joined) return joined;
      continue;
    }
    if (typeof value === 'object') continue;
    const cleaned = String(value).trim();
    if (!cleaned || cleaned === '[]') continue;
    return cleaned;
  }
  return '';
}

export function numberValue(record: ApiRecord | undefined, ...keys: string[]): number | undefined {
  const item = record ?? {};
  for (const key of keys) {
    const value = item[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
}

export function valueOrDash(value: unknown): string {
  if (value === undefined || value === null) return '—';
  if (Array.isArray(value)) {
    const joined = value.map((entry) => String(entry ?? '').trim()).filter(Boolean).join(', ');
    return joined || '—';
  }
  const cleaned = String(value).trim();
  if (!cleaned || cleaned === '[]') return '—';
  return cleaned;
}

export function statusPillClass(value: unknown): string {
  return statusPillClassName(value);
}

export function friendlyStatus(value: unknown): string {
  return friendlyLabel(value);
}

export function packageId(pkg: ApiRecord): string {
  return text(pkg, 'packageId', 'id', 'packageName', 'evidencePackageId', 'finalAuditorPackageId');
}

export function packageName(pkg: ApiRecord): string {
  const name = text(pkg, 'packageName', 'evidencePackageFileName', 'finalAuditorPackageName', 'packageId');
  return name.replace(/^AWS#\d{12}#PACKAGE#/i, '') || 'Evidence package';
}

export function packageStatus(pkg: ApiRecord): string {
  return friendlyStatus(text(pkg, 'displayStatus', 'status', 'packageLifecycleStatus', 'jobStatus'));
}

export function packageUpdatedAt(pkg: ApiRecord): string {
  return text(pkg, 'updatedAt', 'packageUpdatedAt', 'createdAt', 'generatedUtc');
}


export function packageUpdatedAtDisplay(pkg: ApiRecord | undefined): string {
  return formatLocalDateTime(packageUpdatedAt(pkg ?? {}));
}

export function packageType(pkg: ApiRecord): string {
  return text(pkg, 'packageType', 'type');
}

export function packageTypeLabel(pkg: ApiRecord): string {
  return friendlyLabel(packageType(pkg));
}

export function packageStage(pkg: ApiRecord): string {
  return text(pkg, 'packageStage', 'stage');
}

export function packageStageLabel(pkg: ApiRecord): string {
  return friendlyLabel(packageStage(pkg));
}

export function controlId(control: ApiRecord): string {
  return text(control, 'controlId', 'id', 'control');
}

export function controlStatus(control: ApiRecord): string {
  return text(control, 'status', 'controlStatus', 'finalAssessmentStatus').toUpperCase() || 'UNKNOWN';
}

export function stringList(record: ApiRecord | undefined, ...keys: string[]): string[] {
  const item = record ?? {};
  for (const key of keys) {
    const value = item[key];
    if (Array.isArray(value)) {
      const values = value.map((entry) => String(entry ?? '').trim()).filter(Boolean);
      if (values.length > 0) return values;
    }
    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }
  }
  return [];
}

function joinReasonAndAction(reason: string, action: string): string {
  const cleanedReason = reason.trim();
  const cleanedAction = action.trim();
  if (cleanedReason && cleanedAction && cleanedReason !== cleanedAction) {
    return `Reason: ${cleanedReason} Action: ${cleanedAction}`;
  }
  return cleanedReason || cleanedAction;
}

export function controlEnhancedReasonAction(control: ApiRecord): string {
  // Client portal must use only client-safe reason/action fields.
  // Admin/internal fields such as adminReason, adminAction, adminEvidenceGap,
  // reasonActionRecord, and internal implementation details must not be read or displayed here.
  const clientReason = text(control, 'clientReason', 'displayReason');
  const clientAction = text(control, 'clientAction', 'recommendedAction', 'actionRequiredReason');
  return joinReasonAndAction(clientReason, clientAction);
}

export function controlReasonActionMetadata(control: ApiRecord): string {
  const parts = [
    text(control, 'actionCategory'),
    text(control, 'ownerHint'),
    text(control, 'reasonActionConfidence'),
  ].filter(Boolean);
  return parts.join(' · ');
}

export function controlReasonAction(control: ApiRecord): string {
  const status = controlStatus(control);
  const enhanced = controlEnhancedReasonAction(control);
  if (enhanced) return enhanced;

  const openItems = stringList(control, 'openItems');

  if (status === 'PARTIAL' || status === 'FAIL' || status === 'FAILED') {
    return (
      text(control, 'clientAction', 'recommendedAction', 'actionRequiredReason')
      || openItems[0]
      || text(control, 'clientReason', 'displayReason', 'reasonAction', 'reason', 'statusReason', 'reportUse', 'technicalReason')
      || 'Action required. Review control evidence details.'
    );
  }

  if (status === 'PASS') {
    return (
      text(control, 'clientReason', 'displayReason', 'reasonAction')
      || text(control, 'reason', 'statusReason', 'technicalReason', 'reportUse')
      || 'Evidence supports this control.'
    );
  }

  if (status === 'NOT_APPLICABLE') {
    return (
      text(control, 'clientReason', 'displayReason', 'reasonAction')
      || text(control, 'reason', 'completedEvidenceDecision', 'clientDeclarationDecision', 'knownInterpretation')
      || 'Marked not applicable based on validated scoping or applicability evidence.'
    );
  }

  return (
    text(control, 'clientReason', 'displayReason', 'reasonAction', 'reason', 'statusReason', 'reportUse', 'technicalReason')
    || 'Review evidence details.'
  );
}

function displayList(values: string[]): string {
  return values.filter(Boolean).join(', ');
}

export function documentId(document: ApiRecord): string {
  return text(document, 'documentId', 'documentKey', 'documentName', 'file', 'path', 'id');
}

export function documentStatus(document: ApiRecord): string {
  return text(document, 'status', 'documentStatus', 'validationStatus').toUpperCase() || 'UNKNOWN';
}

export function documentDisplayPath(document: ApiRecord): string {
  return text(document, 'path', 'documentPath', 'file', 'documentFileName', 'documentKey', 's3Key', 'documentName', 'documentId');
}

export function documentControls(document: ApiRecord): string {
  const values = stringList(document, 'controlsSupported', 'controlIds', 'controls', 'mappedControls', 'relatedControls', 'controlMappings');
  return displayList(values);
}

export function documentReasonAction(document: ApiRecord): string {
  const status = documentStatus(document);
  const controls = documentControls(document);
  const documentName = documentId(document);
  const explicit = text(
    document,
    'reasonAction',
    'recommendedAction',
    'actionRequiredReason',
    'reason',
    'statusReason',
    'reportUse',
    'intendedOutcome',
  );

  if (explicit) return explicit;

  if (['PENDING', 'MISSING', 'REJECTED', 'PARTIAL', 'FAILED', 'FAIL', 'CLIENT_ACTION_REQUIRED', 'CLIENT_UPLOAD_REQUIRED'].includes(status)) {
    if (controls) return `Complete, validate, and upload this evidence document for mapped controls: ${controls}.`;
    if (documentName) return `Complete, validate, and upload ${documentName}.`;
    return 'Complete, validate, and upload this evidence document.';
  }

  return '';
}


export function isFinalAuditorPackageRecord(pkg: ApiRecord | undefined): boolean {
  const item = pkg ?? {};
  const type = packageType(item).toUpperCase();
  const stage = packageStage(item).toUpperCase();
  const status = text(item, 'status', 'displayStatus', 'packageLifecycleStatus').toUpperCase();
  const name = [packageId(item), packageName(item)].join(' ').toUpperCase();

  if (type === 'TECHNICAL_EVIDENCE_PACKAGE' || stage === 'TECHNICAL_BASELINE') return false;
  return type === 'FINAL_AUDITOR_PACKAGE'
    || stage === 'FINAL_AUDITOR_READY'
    || stage === 'AUDITOR_PACKAGE_READY'
    || status.includes('AUDITOR_PACKAGE_READY')
    || status.includes('FINAL_AUDITOR_READY')
    || name.includes('AUDITOR-EVIDENCE-PACKAGE')
    || name.includes('AUDITOR EVIDENCE PACKAGE');
}

export function canClientDownloadPackage(pkg: ApiRecord | undefined): boolean {
  return isFinalAuditorPackageRecord(pkg);
}

export function downloadUrl(pkg: ApiRecord): string {
  return text(
    pkg,
    'finalAuditorPackageDownloadUrl',
    'downloadUrl',
    'presignedDownloadUrl',
    'finalPackageDownloadUrl',
    'url',
  );
}

export function packageCounts(pkg: ApiRecord) {
  const pass = numberValue(pkg, 'passCount', 'pass', 'finalReadinessPassControls', 'PASS') ?? 0;
  const partial = numberValue(pkg, 'partialCount', 'partial', 'finalReadinessPartialControls', 'PARTIAL') ?? 0;
  const fail = numberValue(pkg, 'failCount', 'failedCount', 'fail', 'finalReadinessFailControls', 'FAIL') ?? 0;
  const notApplicable = numberValue(pkg, 'notApplicableCount', 'notApplicable', 'finalReadinessNotApplicableControls', 'NOT_APPLICABLE') ?? 0;
  const total = numberValue(pkg, 'totalControls', 'total', 'finalReadinessTotalControls') ?? pass + partial + fail + notApplicable;
  return { pass, partial, fail, notApplicable, total };
}

function scopeSummaryRecord(pkg: ApiRecord | undefined): ApiRecord {
  return asRecord((pkg ?? {}).scopeSummary);
}

function scopeNumber(pkg: ApiRecord | undefined, nestedKey: string, ...flatKeys: string[]): number {
  const nested = scopeSummaryRecord(pkg);
  return numberValue(pkg, ...flatKeys) ?? numberValue(nested, nestedKey) ?? 0;
}

export function packageScopeCounts(pkg: ApiRecord | undefined) {
  const nested = scopeSummaryRecord(pkg);
  const total = scopeNumber(pkg, 'totalAssets', 'scopeTotalAssets');
  const inScope = scopeNumber(pkg, 'inScopeAssets', 'scopeInScopeAssets');
  const outOfScope = scopeNumber(pkg, 'outOfScopeAssets', 'scopeOutOfScopeAssets');
  const unknown = scopeNumber(pkg, 'unknownScopeAssets', 'scopeUnknownScopeAssets');
  const cui = scopeNumber(pkg, 'cuiAssets', 'scopeCuiAssets');
  const securityProtection = scopeNumber(pkg, 'securityProtectionAssets', 'scopeSecurityProtectionAssets');
  const clientEvidencePackage = scopeNumber(pkg, 'clientEvidencePackageAssets', 'scopeClientEvidencePackageAssets');
  const evidenceEligible = scopeNumber(pkg, 'evidenceEligibleAssets', 'scopeEvidenceEligibleAssets');
  const missingTags = scopeNumber(pkg, 'missingRequiredTagFindings', 'scopeMissingRequiredTagFindings');
  const invalidTags = scopeNumber(pkg, 'invalidTagFindings', 'scopeInvalidTagFindings');
  const validationStatus = text(pkg, 'scopeValidationStatus') || text(nested, 'validationStatus');
  return { total, inScope, outOfScope, unknown, cui, securityProtection, clientEvidencePackage, evidenceEligible, missingTags, invalidTags, validationStatus };
}

function boolValue(record: ApiRecord | undefined, key: string): boolean {
  const raw = (record ?? {})[key];
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') return raw.trim().toLowerCase() === 'true';
  return false;
}

export function packageScopeReviewCounts(pkg: ApiRecord | undefined) {
  const nested = scopeSummaryRecord(pkg);
  const awsManaged = scopeNumber(pkg, 'unknownScopeLikelyAwsManaged', 'scopeUnknownScopeLikelyAwsManaged');
  const controlTower = scopeNumber(pkg, 'unknownScopeLikelyControlTower', 'scopeUnknownScopeLikelyControlTower');
  const cdkBootstrap = scopeNumber(pkg, 'unknownScopeLikelyCdkBootstrap', 'scopeUnknownScopeLikelyCdkBootstrap');
  const platformSupport = scopeNumber(pkg, 'unknownScopeLikelyPlatformSupport', 'scopeUnknownScopeLikelyPlatformSupport');
  const clientCreated = scopeNumber(pkg, 'unknownScopeClientCreatedUnknown', 'scopeUnknownScopeClientCreatedUnknown');
  const requiresReview = scopeNumber(pkg, 'unknownScopeRequiresClientReview', 'scopeUnknownScopeRequiresClientReview');
  const unknown = scopeNumber(pkg, 'unknownScopeAssets', 'scopeUnknownScopeAssets');
  const classificationPresent = boolValue(pkg, 'scopeUnknownScopeClassificationPresent')
    || text(nested, 'unknownScopeClassificationPresent').toLowerCase() === 'true';
  const likelyManagedOrPlatform = awsManaged + controlTower + cdkBootstrap + platformSupport;
  return { classificationPresent, unknown, awsManaged, controlTower, cdkBootstrap, platformSupport, clientCreated, requiresReview, likelyManagedOrPlatform };
}

export function hasPackageScopeReviewContext(pkg: ApiRecord | undefined): boolean {
  const counts = packageScopeReviewCounts(pkg);
  return Boolean(
    counts.classificationPresent
    || counts.awsManaged
    || counts.controlTower
    || counts.cdkBootstrap
    || counts.platformSupport
    || counts.clientCreated
    || counts.requiresReview
  );
}

export function packageScopeReviewMessages(pkg: ApiRecord | undefined): string[] {
  if (!hasPackageScopeReviewContext(pkg)) return [];
  const counts = packageScopeReviewCounts(pkg);
  return [
    `${counts.unknown} assets need scope review.`,
    `${counts.likelyManagedOrPlatform} appear to be AWS-managed or platform-generated.`,
    `${counts.clientCreated} appear to be client-created unknown assets.`,
    `${counts.requiresReview} require client/SURELINC review before assessor use.`,
  ];
}

export function hasPackageScopeMetadata(pkg: ApiRecord | undefined): boolean {
  const counts = packageScopeCounts(pkg);
  return Boolean(
    text(pkg, 'resourceScopeMetadataPresent').toLowerCase() === 'true'
    || text(pkg, 'scopeValidationStatus')
    || Object.keys(scopeSummaryRecord(pkg)).length > 0
    || counts.total
    || counts.inScope
    || counts.outOfScope
    || counts.unknown
    || counts.missingTags
    || counts.invalidTags
  );
}

export function packageScopeSummary(pkg: ApiRecord | undefined): string {
  if (!hasPackageScopeMetadata(pkg)) return '—';
  const counts = packageScopeCounts(pkg);
  const parts = [
    `In ${counts.inScope}`,
    `Out ${counts.outOfScope}`,
    `Unknown ${counts.unknown}`,
  ];
  if (counts.total) parts.push(`Total ${counts.total}`);
  return parts.join(' / ');
}

export function packageScopeExceptionSummary(pkg: ApiRecord | undefined): string {
  if (!hasPackageScopeMetadata(pkg)) return '—';
  const counts = packageScopeCounts(pkg);
  const parts = [];
  if (counts.unknown) parts.push(`${counts.unknown} unknown scope`);
  if (counts.missingTags) parts.push(`${counts.missingTags} missing tag finding${counts.missingTags === 1 ? '' : 's'}`);
  if (counts.invalidTags) parts.push(`${counts.invalidTags} invalid tag finding${counts.invalidTags === 1 ? '' : 's'}`);
  if (counts.validationStatus) parts.push(`Validation ${counts.validationStatus}`);
  return parts.length ? parts.join(' / ') : 'No scope exceptions indexed';
}

export function packageScopeAssetSummary(pkg: ApiRecord | undefined): string {
  if (!hasPackageScopeMetadata(pkg)) return '—';
  const counts = packageScopeCounts(pkg);
  const parts = [];
  if (counts.cui) parts.push(`${counts.cui} CUI`);
  if (counts.securityProtection) parts.push(`${counts.securityProtection} security protection`);
  if (counts.clientEvidencePackage) parts.push(`${counts.clientEvidencePackage} evidence package`);
  if (counts.evidenceEligible) parts.push(`${counts.evidenceEligible} evidence eligible`);
  return parts.length ? parts.join(' / ') : '—';
}

export function countSignedDocuments(documents: ApiRecord[]): number {
  return documents.filter((document) => text(document, 'signed', 'isSigned').toUpperCase() === 'TRUE' || documentStatus(document).includes('SIGNED')).length;
}

export function isActionRequired(record: ApiRecord): boolean {
  const joined = [
    text(record, 'status', 'documentStatus', 'controlStatus', 'actionStatus'),
    text(record, 'actionRequired', 'clientActionRequired', 'requiresClientAction'),
    text(record, 'intendedOutcome', 'clientReason', 'clientAction', 'displayReason', 'recommendedAction', 'actionRequiredReason', 'reason'),
  ].join(' ').toUpperCase();
  return joined.includes('ACTION_REQUIRED')
    || joined.includes('CLIENT ACTION')
    || joined.includes('HUMAN_PROCESS')
    || joined.includes('NEEDS_REMEDIATION')
    || joined.includes('PENDING')
    || joined.includes('PARTIAL')
    || joined.includes('FINAL_ASSESSOR_REVIEW_PENDING');
}

export function matchesControlFilter(control: ApiRecord, filter: ControlFilter): boolean {
  if (filter === 'ALL') return true;
  const status = controlStatus(control);
  const haystack = JSON.stringify(control).toUpperCase();
  if (['PASS', 'PARTIAL', 'FAIL', 'NOT_APPLICABLE'].includes(filter)) {
    return status === filter;
  }
  if (filter === 'CLIENT_ACTION_REQUIRED') return isActionRequired(control);
  if (filter === 'HUMAN_PROCESS_REQUIRED') return haystack.includes('HUMAN') || haystack.includes('PROCESS');
  if (filter === 'POSSIBLE_NOT_APPLICABLE') return haystack.includes('POSSIBLE_NOT_APPLICABLE') || haystack.includes('NOT APPLICABLE') || haystack.includes('NOT_APPLICABLE');
  if (filter === 'FINAL_ASSESSOR_REVIEW_PENDING') return haystack.includes('ASSESSOR') || haystack.includes('REVIEW_PENDING') || haystack.includes('FINAL_REVIEW');
  return true;
}

export function latestPackageFromResponse(response: unknown, key: 'technical' | 'final'): ApiRecord {
  const root = asRecord(response);
  const candidates = key === 'technical'
    ? ['latestTechnicalEvidencePackage', 'latestTechnicalPackage', 'technicalEvidencePackage', 'technicalPackage']
    : ['latestFinalAuditorPackage', 'finalAuditorPackage', 'latestAuditorPackage'];

  for (const candidate of candidates) {
    const record = asRecord(root[candidate]);
    if (Object.keys(record).length > 0) return record;
  }

  // API versions that return a latest map use the package type as the key and
  // nest the resolved package under .package. Prefer that resolved package over
  // the LATEST# pointer, because the pointer does not contain readiness counts.
  const latest = asRecord(root.latest);
  const latestKeys = key === 'technical'
    ? ['TECHNICAL_EVIDENCE_PACKAGE', 'technicalEvidencePackage', 'technicalPackage']
    : ['FINAL_AUDITOR_PACKAGE', 'finalAuditorPackage', 'auditorPackage'];

  for (const latestKey of latestKeys) {
    const latestEntry = asRecord(latest[latestKey]);
    const resolvedPackage = asRecord(latestEntry.package);
    if (Object.keys(resolvedPackage).length > 0) return resolvedPackage;

    const directLatestRecord = asRecord(latest[latestKey]);
    if (Object.keys(directLatestRecord).length > 0 && !String(directLatestRecord.packageId ?? '').startsWith('LATEST#')) {
      return directLatestRecord;
    }
  }

  const packages = recordsFromResponse(response, ['packages', 'items']);
  const matcher = key === 'technical' ? 'TECHNICAL_EVIDENCE_PACKAGE' : 'FINAL_AUDITOR_PACKAGE';
  return packages.find((pkg) => packageType(pkg).toUpperCase() === matcher) ?? {};
}

export function currentReadinessPackageFromResponse(response: unknown): ApiRecord {
  const root = asRecord(response);
  const direct = asRecord(root.currentReadinessPackage);
  if (Object.keys(direct).length > 0) return direct;
  const current = asRecord(root.currentPackage);
  if (Object.keys(current).length > 0) return current;

  const latestFinal = latestPackageFromResponse(response, 'final');
  if (Object.keys(latestFinal).length > 0) return latestFinal;

  return latestPackageFromResponse(response, 'technical');
}
